/** DIVETTA MILANO R7 — collection utility + desktop presentation adapter.
 *  R6 remains the sole owner of product data, active scene, forms, variants,
 *  cart actions, filters, sorting and mobile social-scroll/return position.
 */
(() => {
  'use strict';

  const KEY = '__DivettaR7Collection';
  window[KEY]?.destroy?.();

  const controller = new AbortController();
  const { signal } = controller;
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const utilityRecords = new Map();
  const desktopContexts = new Map();
  const RAIL_ENTER_MS = 180;
  const RAIL_VISIBLE_MS = 3000;
  const RAIL_EXIT_MS = 220;

  const clearRailTimer = (context, key) => {
    if (!context[key]) return;
    window.clearTimeout(context[key]);
    context[key] = 0;
  };

  const setRailInteractive = (context, interactive) => {
    const { rail, buttons } = context;
    if (!rail) return;
    rail.setAttribute('aria-hidden', String(!interactive));
    rail.toggleAttribute('inert', !interactive);
    buttons.forEach((button) => { button.tabIndex = interactive && !button.hidden ? 0 : -1; });
  };

  const scheduleRailExit = (context, delay = RAIL_VISIBLE_MS) => {
    clearRailTimer(context, 'exitTimer');
    context.remainingVisible = Math.max(0, delay);
    context.hideAt = performance.now() + context.remainingVisible;
    context.exitTimer = window.setTimeout(() => {
      context.exitTimer = 0;
      const { rail } = context;
      if (!rail || rail.matches(':focus-within')) return;
      rail.dataset.r7RailState = 'exiting';
      rail.style.pointerEvents = 'none';
      context.settleTimer = window.setTimeout(() => {
        context.settleTimer = 0;
        rail.dataset.r7RailState = 'hidden';
        rail.style.removeProperty('pointer-events');
        setRailInteractive(context, false);
      }, RAIL_EXIT_MS);
    }, context.remainingVisible);
  };

  const hideRail = (context, { immediate = false } = {}) => {
    const { rail, scenes, activeIndex } = context;
    if (!rail) return;
    ['enterTimer', 'exitTimer', 'settleTimer'].forEach((key) => clearRailTimer(context, key));
    if (immediate) {
      if (rail.contains(document.activeElement)) {
        const destination = scenes[activeIndex]?.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
        destination?.focus?.({ preventScroll: true });
      }
      rail.dataset.r7RailState = 'hidden';
      rail.style.removeProperty('pointer-events');
      setRailInteractive(context, false);
      return;
    }
    rail.dataset.r7RailState = 'exiting';
    rail.style.pointerEvents = 'none';
    context.settleTimer = window.setTimeout(() => {
      context.settleTimer = 0;
      if (rail.contains(document.activeElement)) {
        const destination = scenes[activeIndex]?.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
        destination?.focus?.({ preventScroll: true });
      }
      rail.dataset.r7RailState = 'hidden';
      rail.style.removeProperty('pointer-events');
      setRailInteractive(context, false);
    }, RAIL_EXIT_MS);
  };

  const revealRail = (context) => {
    const { rail } = context;
    if (!rail) return;
    ['enterTimer', 'exitTimer', 'settleTimer'].forEach((key) => clearRailTimer(context, key));
    context.remainingVisible = RAIL_VISIBLE_MS;
    setRailInteractive(context, true);
    rail.style.removeProperty('pointer-events');
    rail.dataset.r7RailState = 'entering';
    context.enterTimer = window.setTimeout(() => {
      context.enterTimer = 0;
      if (rail.matches(':focus-within')) {
        rail.dataset.r7RailState = 'focus-paused';
        return;
      }
      rail.dataset.r7RailState = 'visible';
      scheduleRailExit(context, Math.max(0, RAIL_VISIBLE_MS - RAIL_ENTER_MS));
    }, RAIL_ENTER_MS);
  };

  const mountUtility = (scope = document) => {
    scope.querySelectorAll('[data-ldx-utility]:not([data-r7-utility-mounted])').forEach((utility, index) => {
      const localController = new AbortController();
      const localSignal = localController.signal;
      utility.dataset.r7UtilityMounted = 'true';
      utility.classList.add('r7-collection-utility');
      const uid = utility.id || `R7CollectionUtility-${index + 1}`;
      utility.id = uid;
      const sheetQuery = window.matchMedia('(max-width: 47.99rem)');

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'r7-collection-utility-trigger';
      trigger.textContent = 'Cerca e filtra';
      trigger.setAttribute('aria-controls', uid);
      trigger.setAttribute('aria-expanded', 'false');

      const backdrop = document.createElement('button');
      backdrop.type = 'button';
      backdrop.className = 'r7-collection-utility-backdrop';
      backdrop.setAttribute('aria-label', 'Chiudi Cerca e filtra');
      backdrop.hidden = true;

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'r7-collection-utility-close';
      close.setAttribute('aria-label', 'Chiudi Cerca e filtra');
      close.innerHTML = '<span>Cerca e filtra</span><span aria-hidden="true">×</span>';
      utility.prepend(close);
      utility.before(trigger, backdrop);

      let returnFocus = trigger;
      const syncMode = () => {
        const sheetMode = sheetQuery.matches;
        if (sheetMode) {
          utility.setAttribute('role', 'dialog');
          utility.setAttribute('aria-modal', 'true');
          utility.setAttribute('aria-label', 'Cerca e filtra');
          utility.setAttribute('aria-hidden', String(!utility.classList.contains('is-r7-open')));
          utility.toggleAttribute('inert', !utility.classList.contains('is-r7-open'));
        } else {
          utility.classList.remove('is-r7-open');
          utility.removeAttribute('role');
          utility.removeAttribute('aria-modal');
          utility.removeAttribute('aria-label');
          utility.removeAttribute('aria-hidden');
          utility.removeAttribute('inert');
          trigger.setAttribute('aria-expanded', 'false');
          backdrop.hidden = true;
          document.body.classList.remove('r7-collection-sheet-open');
        }
      };
      const setOpen = (open, source = trigger) => {
        if (open && !sheetQuery.matches) return;
        returnFocus = source || trigger;
        utility.classList.toggle('is-r7-open', open);
        utility.setAttribute('aria-hidden', String(!open));
        utility.toggleAttribute('inert', !open);
        trigger.setAttribute('aria-expanded', String(open));
        backdrop.hidden = !open;
        document.body.classList.toggle('r7-collection-sheet-open', open);
        if (open) {
          window.setTimeout(() => utility.querySelector('input[type="search"], select, button:not(.r7-collection-utility-close)')?.focus(), 30);
        } else if (sheetQuery.matches) {
          returnFocus?.focus?.({ preventScroll: true });
        }
      };

      trigger.addEventListener('click', () => setOpen(true, trigger), { signal: localSignal });
      close.addEventListener('click', () => setOpen(false), { signal: localSignal });
      backdrop.addEventListener('click', () => setOpen(false), { signal: localSignal });
      document.addEventListener('r7:collection-utility:open', (event) => setOpen(true, event.detail?.trigger || trigger), { signal: localSignal });
      document.addEventListener('keydown', (event) => {
        if (!utility.classList.contains('is-r7-open')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          setOpen(false);
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = Array.from(utility.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
          .filter((node) => !node.hidden && node.getClientRects().length > 0);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }, { signal: localSignal });
      sheetQuery.addEventListener?.('change', () => {
        setOpen(false);
        syncMode();
      }, { signal: localSignal });
      syncMode();

      const cleanup = () => {
        localController.abort();
        trigger.remove();
        backdrop.remove();
        close.remove();
        utility.classList.remove('r7-collection-utility', 'is-r7-open');
        utility.removeAttribute('role');
        utility.removeAttribute('aria-modal');
        utility.removeAttribute('aria-label');
        utility.removeAttribute('aria-hidden');
        utility.removeAttribute('inert');
        delete utility.dataset.r7UtilityMounted;
        utilityRecords.delete(utility);
      };
      utilityRecords.set(utility, cleanup);
    });
  };

  const cleanupUtilities = (scope = null) => {
    Array.from(utilityRecords.entries()).forEach(([utility, cleanup]) => {
      if (!utility.isConnected || scope === utility || scope?.contains?.(utility)) cleanup();
    });
  };

  const productLabel = (scene, index) => {
    const label = scene.dataset.productTitle
      || scene.querySelector('.ldx-sakazuki-scene__title, .ldx-capelli-chapter__title, .ld-estate-story__chapter-title')?.textContent
      || `Prodotto ${index + 1}`;
    return label.replace(/\s+/g, ' ').trim();
  };

  const detectContexts = (scope = document) => {
    const contexts = [];
    const seen = new Set();

    const add = (root, story, scenes, type) => {
      if (!root || !story || !scenes.length || seen.has(root)) return;
      seen.add(root);
      contexts.push({ root, story, scenes, type });
    };

    const searchRoot = scope.matches?.('[data-r7-route="collection"]') ? scope : scope;

    searchRoot.querySelectorAll?.('[data-ldx-story]').forEach((story) => {
      const root = story.closest('ldx-sakazuki-grid') || story.parentElement;
      if (root?.matches?.('[data-ldx-capelli-products]')) return;
      add(root, story, Array.from(story.querySelectorAll('[data-ldx-product-scene]')), 'sakazuki');
    });

    searchRoot.querySelectorAll?.('[data-capelli-story]').forEach((story) => {
      const root = story.closest('ldx-sakazuki-grid') || story.closest('[data-ldx-capelli-products]') || story.parentElement;
      add(root, story, Array.from(story.querySelectorAll('[data-capelli-chapter]')), 'capelli');
    });

    searchRoot.querySelectorAll?.('[data-ld-estate-story]').forEach((root) => {
      const story = root.querySelector('[data-ld-story-chapters]') || root;
      add(root, story, Array.from(story.querySelectorAll('[data-ld-story-chapter]')), 'estate');
    });

    return contexts;
  };

  const syncRailFromR6 = (context, preferredScene = null) => {
    const { root, scenes, type, buttons } = context;
    let active = preferredScene && scenes.includes(preferredScene) ? preferredScene : null;

    if (!active && type === 'estate') {
      const activeLink = root.querySelector('[data-ld-story-nav-link].is-active');
      const index = Number(activeLink?.dataset.chapterIndex);
      if (Number.isFinite(index)) active = scenes[index] || null;
    }

    if (!active) {
      active = scenes.find((scene) => scene.classList.contains('is-active')) || null;
    }

    if (!active && root.dataset.activeProductId) {
      active = scenes.find((scene) => scene.dataset.productId === root.dataset.activeProductId) || null;
    }

    if (!active) active = scenes.find((scene) => !scene.hidden) || scenes[0] || null;
    if (!active) return;

    const activeIndex = scenes.indexOf(active);
    const activeProductId = active.dataset.productId || root.dataset.activeProductId || `index:${activeIndex}`;
    const activeProductChanged = activeProductId !== context.lastAnnouncedActiveProductId;
    context.activeIndex = activeIndex;
    root.dataset.r7CollectionActive = String(activeIndex);
    buttons.forEach((button, index) => {
      const scene = scenes[index];
      const isActive = index === activeIndex;
      const hidden = Boolean(scene.hidden);
      if (button.hidden !== hidden) button.hidden = hidden;
      const item = button.parentElement;
      item?.classList.toggle('is-r7-active', isActive && !hidden);
      item?.classList.toggle('is-r7-neighbor', Math.abs(index - activeIndex) <= 1 && !hidden);
      button.toggleAttribute('aria-current', isActive);
      button.setAttribute('aria-pressed', String(isActive));
      button.tabIndex = context.rail?.getAttribute('aria-hidden') === 'false' && !button.hidden ? 0 : -1;
    });
    if (activeProductChanged) {
      context.lastAnnouncedActiveProductId = activeProductId;
      revealRail(context);
    }
  };

  const buildRail = (context) => {
    const { root, story, scenes, type, localController } = context;
    if (type === 'estate' && root.querySelector('[data-ld-story-nav]')) return [];

    const rail = document.createElement('nav');
    rail.className = 'r7-collection-state-rail';
    rail.dataset.r7CollectionStateRail = '';
    rail.dataset.r7RailState = 'hidden';
    rail.setAttribute('aria-label', 'Esplora i prodotti della collezione');
    rail.setAttribute('aria-hidden', 'true');
    rail.setAttribute('inert', '');

    const list = document.createElement('ol');
    scenes.forEach((scene, index) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      const label = productLabel(scene, index);
      button.type = 'button';
      button.dataset.r7CollectionStateButton = String(index);
      button.setAttribute('aria-label', `Vai a ${label}`);
      button.setAttribute('aria-pressed', 'false');
      button.innerHTML = `<span>${String(index + 1).padStart(2, '0')}</span><b></b>`;
      button.querySelector('b').textContent = label;
      button.addEventListener('click', () => {
        scene.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: type === 'capelli' ? 'center' : 'start' });
      }, { signal: localController.signal });
      item.append(button);
      list.append(item);
    });
    rail.append(list);
    story.before(rail);
    context.rail = rail;
    const buttons = Array.from(rail.querySelectorAll('[data-r7-collection-state-button]'));
    buttons.forEach((button) => { button.tabIndex = -1; });
    rail.addEventListener('focusin', () => {
      if (rail.dataset.r7RailState === 'hidden' || rail.dataset.r7RailState === 'exiting') return;
      context.remainingVisible = context.hideAt ? Math.max(0, context.hideAt - performance.now()) : RAIL_VISIBLE_MS;
      clearRailTimer(context, 'exitTimer');
      clearRailTimer(context, 'enterTimer');
      rail.dataset.r7RailState = 'focus-paused';
    }, { signal: localController.signal });
    rail.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!rail.isConnected || rail.matches(':focus-within') || rail.dataset.r7RailState !== 'focus-paused') return;
        rail.dataset.r7RailState = 'visible';
        scheduleRailExit(context, context.remainingVisible || RAIL_VISIBLE_MS);
      }, 0);
    }, { signal: localController.signal });
    rail.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      hideRail(context, { immediate: true });
    }, { signal: localController.signal });
    return buttons;
  };

  const mountDesktopContext = ({ root, story, scenes, type }) => {
    if (desktopContexts.has(root)) return;
    const localController = new AbortController();
    const context = {
      root, story, scenes, type, localController, rail: null, buttons: [], observer: null,
      activeIndex: -1, lastAnnouncedActiveProductId: '', enterTimer: 0, exitTimer: 0, settleTimer: 0, hideAt: 0, remainingVisible: RAIL_VISIBLE_MS
    };

    root.classList.add('is-r7-collection-choreography');
    root.dataset.r7CollectionChoreography = type;
    root.dataset.r7Opening = type === 'estate' ? 'mosaic' : 'state';
    root.dataset.r7SpineState = type === 'capelli' ? '2' : type === 'estate' ? '1' : '0';
    root.dataset.r7SpineIntensity = 'b';
    story.classList.add('r7-collection-state-story');
    scenes.forEach((scene, index) => {
      scene.dataset.r7CollectionState = '';
      scene.dataset.r7CollectionStateIndex = String(index);
    });

    context.buttons = buildRail(context);

    root.addEventListener('ld:social-feed:product-view', (event) => {
      const scene = scenes.find((candidate) => candidate.dataset.productId === String(event.detail?.productId || ''))
        || scenes.find((candidate) => Number(candidate.dataset.sceneIndex) === Number(event.detail?.index));
      syncRailFromR6(context, scene || null);
    }, { signal: localController.signal });

    if ('MutationObserver' in window) {
      context.observer = new MutationObserver(() => syncRailFromR6(context));
      context.observer.observe(root, {
        attributes: true,
        attributeFilter: ['data-active-product-id', 'data-active-scene-index']
      });
      scenes.forEach((scene) => context.observer.observe(scene, {
        attributes: true,
        attributeFilter: ['class', 'hidden']
      }));
    }

    syncRailFromR6(context);
    desktopContexts.set(root, context);
    window.DivettaZorge?.refresh?.();
  };

  const unmountDesktopContext = (context) => {
    const { root, story, scenes, localController, observer, rail } = context;
    localController.abort();
    observer?.disconnect();
    ['enterTimer', 'exitTimer', 'settleTimer'].forEach((key) => clearRailTimer(context, key));
    rail?.remove();
    root.classList.remove('is-r7-collection-choreography');
    delete root.dataset.r7CollectionChoreography;
    delete root.dataset.r7CollectionActive;
    delete root.dataset.r7Opening;
    delete root.dataset.r7SpineState;
    delete root.dataset.r7SpineIntensity;
    story.classList.remove('r7-collection-state-story');
    scenes.forEach((scene) => {
      delete scene.dataset.r7CollectionState;
      delete scene.dataset.r7CollectionStateIndex;
    });
    desktopContexts.delete(root);
    window.DivettaZorge?.refresh?.();
  };

  const unmountDesktop = () => Array.from(desktopContexts.values()).forEach(unmountDesktopContext);

  const mountDesktop = (scope = document) => {
    if (!desktopQuery.matches || reduceMotion.matches) {
      unmountDesktop();
      return;
    }
    detectContexts(scope).forEach(mountDesktopContext);
    Array.from(desktopContexts.values()).forEach((context) => {
      if (!document.contains(context.root)) unmountDesktopContext(context);
      else syncRailFromR6(context);
    });
  };

  const refresh = (scope = document) => {
    mountUtility(scope);
    mountDesktop(scope);
  };

  const onModeChange = () => {
    if (desktopQuery.matches && !reduceMotion.matches) mountDesktop(document);
    else unmountDesktop();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => refresh(document), { once: true, signal });
  else refresh(document);

  document.addEventListener('shopify:section:load', (event) => refresh(event.target), { signal });
  document.addEventListener('shopify:section:unload', (event) => {
    cleanupUtilities(event.target);
    window.requestAnimationFrame(() => mountDesktop(document));
  }, { signal });
  document.addEventListener('shopify:section:reorder', () => window.requestAnimationFrame(() => mountDesktop(document)), { signal });
  document.addEventListener('shopify:block:select', (event) => {
    if (!desktopQuery.matches || reduceMotion.matches) return;
    const scene = event.target.closest?.('[data-r7-collection-state]');
    const context = Array.from(desktopContexts.values()).find((candidate) => candidate.scenes.includes(scene));
    if (!scene || !context) return;
    scene.scrollIntoView({ behavior: 'auto', block: context.type === 'capelli' ? 'center' : 'start' });
    syncRailFromR6(context, scene);
  }, { signal });
  desktopQuery.addEventListener?.('change', onModeChange, { signal });
  reduceMotion.addEventListener?.('change', onModeChange, { signal });
  window.addEventListener('pageshow', () => mountDesktop(document), { signal });

  window[KEY] = {
    init: refresh,
    refresh,
    destroy() {
      controller.abort();
      unmountDesktop();
      Array.from(utilityRecords.values()).forEach((cleanup) => cleanup());
      document.body.classList.remove('r7-collection-sheet-open');
      delete window[KEY];
    }
  };
})();
